import { useEffect, useState } from 'react';
import { useMediaQuery } from './useMediaQuery';

/**
 * Is this session driven by a finger?
 *
 * Two signals, because neither is enough alone:
 *
 * - `(pointer: coarse)` — the PRIMARY input is a finger: iPad, iPhone, a
 *   tablet-mode PC. It is false on a touchscreen laptop with a trackpad,
 *   which is correct for the first paint (the mouse layout is the safe
 *   default there) but wrong the moment that user touches the screen.
 * - A first `touchstart` anywhere — latched for the rest of the session.
 *   Once a finger has been used, the touch affordances stay on; nobody
 *   alternates fast enough for un-latching to matter, and flicker would
 *   read as a bug.
 *
 * Either signal turns it on; nothing turns it off. Callers use it to opt
 * IN to finger-sized targets (grab handles, 44 pt buttons), so `false`
 * always means the mouse layout that shipped before — which is also what
 * jsdom reports, since `useMediaQuery` returns false without `matchMedia`.
 *
 * Deliberately NOT `ontouchstart in window` / `maxTouchPoints`: those are
 * true on every Windows laptop with a touch panel, mouse in hand.
 */
export const useCoarsePointer = () => {
  const coarse = useMediaQuery('(pointer: coarse)');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (touched || typeof window === 'undefined') return undefined;
    const onTouch = () => setTouched(true);
    window.addEventListener('touchstart', onTouch, {
      once: true,
      passive: true,
    });
    return () => window.removeEventListener('touchstart', onTouch);
  }, [touched]);

  return coarse || touched;
};

export default useCoarsePointer;
